import type {IssueFieldWithUnsavedValue} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import type {LabelPickerLabel} from '@github-ui/item-picker-label-relay'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {useCallback, useEffect, useState} from 'react'

import {LABELS} from '../constants/labels'
import {useIssueCreateDataContext} from '../contexts/IssueCreateDataContext'

export type CopyableMetadata = 'Assignees' | 'Fields' | 'Type' | 'Milestone' | 'Labels'

// Stores initial state of all metadata fields
type InitialMetadata = {
  repository?: RepositoryPickerRepository
  assignees: AssigneePickerAssignee[]
  issueType: IssueTypePickerIssueType | null
  issueFields: IssueFieldWithUnsavedValue[]
  milestone: MilestonePickerMilestone | null
  labels: LabelPickerLabel[]
}

const DEFAULT_NOTICE = LABELS.crossRepoIssueDuplicationDefaultWarning

export const useOnRepositoryChange = () => {
  const {
    repository,
    setRepository,
    assignees,
    setAssignees,
    issueType,
    setIssueType,
    issueFields,
    setIssueFields,
    milestone,
    setMilestone,
    labels,
    setLabels,
  } = useIssueCreateDataContext()

  // Store all initial values in a single state object
  const [initialMetadata, setInitialMetadata] = useState<InitialMetadata>({
    repository,
    assignees,
    issueType,
    issueFields,
    milestone,
    labels,
  })
  const [notice, setNotice] = useState<string>(DEFAULT_NOTICE)

  const onRepositoryChange = useCallback(
    (newRepository: RepositoryPickerRepository | undefined) => {
      if (!newRepository) {
        return
      }

      const droppedMetadata: CopyableMetadata[] = []
      const isSameRepo = newRepository.id === initialMetadata.repository?.id
      const isSameOwner = newRepository.owner?.databaseId === initialMetadata.repository?.owner?.databaseId

      if (isSameRepo) {
        // Restore all metadata when returning to the original repository
        setAssignees?.(initialMetadata.assignees)
        setIssueFields?.(initialMetadata.issueFields)
        setIssueType?.(initialMetadata.issueType)
        setMilestone?.(initialMetadata.milestone)
        setLabels?.(initialMetadata.labels)
      } else {
        // Always reset repository-specific metadata
        setMilestone?.(null)
        // Labels will be dropped if they don't exist, however if they do exist in the selected repository, they will copied
        droppedMetadata.push('Milestone', 'Labels')

        if (isSameOwner) {
          // Copy organization-wide metadata when staying in the same org
          setAssignees?.(initialMetadata.assignees)
          setIssueFields?.(initialMetadata.issueFields)
          setIssueType?.(initialMetadata.issueType)
        } else {
          // Clear all organization-specific metadata when changing orgs
          setAssignees?.([])
          setIssueFields?.([])
          setIssueType?.(null)
          droppedMetadata.push('Assignees')

          if (initialMetadata.repository?.owner?.issueTypesEnabled) {
            droppedMetadata.push('Type')
          }
        }
      }

      setNotice(droppedMetadata.length > 0 ? LABELS.crossRepoIssueDuplicationWarning(droppedMetadata) : DEFAULT_NOTICE)
      setRepository(newRepository)
    },
    [initialMetadata, setAssignees, setIssueFields, setIssueType, setLabels, setMilestone, setRepository],
  )

  // Update initial values when user makes changes in the initial repository
  useEffect(() => {
    if (repository?.databaseId === initialMetadata.repository?.databaseId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInitialMetadata({
        repository,
        assignees,
        issueType,
        issueFields,
        milestone,
        labels,
      })
    }
  }, [repository, assignees, issueType, issueFields, milestone, labels, initialMetadata.repository?.databaseId])

  return {onRepositoryChange, notice, sourceRepository: initialMetadata.repository}
}
